import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import CurrencyInput from 'components/field/CurrencyInput';
import DateInput from 'components/field/DateInput';
import ImageInput from 'components/field/ImageInput';
import IntegerInput from 'components/field/IntegerInput';
import PhoneNumber from 'components/field/PhoneNumber';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import BranchFormshort from 'pages/Branches/BranchFormShort';
import ResetPasswordForm from 'pages/Users/ResetPasswordForm';
import WarehouseFormShort from 'pages/Warehouses/WarehouseFormShort';

import { AUTOCOMPLETE, OTOFLEX } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const defaultColSpan = {
	md: 6
};

const smallColSpan = {
	md: 3
};

const DriverForm = NiceModal.create(({ id, mode, modalName, setDriver }) => {
	const [isMinimizing, setIsMinimizing] = useState(false);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					fullName: yup.string().nullable().required(),
					email: yup.string().email().nullable().required(),
					jobTitle: yup.string().nullable(),
					mobileNumber: yup.string().nullable().phoneNumber().optional(),
					warehouseIDs: yup
						.array()
						.nullable()
						.when('branchIDs', {
							is: (branchIDs) => branchIDs?.length > 0,
							then: (schema) => schema,
							otherwise: (schema) => schema.required()
						}),
					branchIDs: yup.array().nullable(),
					shipmentTarget: yup.number().nullable(),
					capacity: yup.number().nullable(),
					onDuty: yup.boolean(),
					avatarPath: yup.string().nullable(),
					lastLatLonUpdated: yup.string().nullable(),
					lat: yup.number().min(-90).max(90).nullable(),
					lng: yup.number().min(-180).max(180).nullable()
				})
				.required(),
		[]
	);

	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const dispatch = useDispatch();
	const { closeModal } = useModalRouter();
	const { Currency } = useLocale();

	const { showSuccess, showError } = useAlert();

	const viewMode = mode === 'view';

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const fillForm = useCallback(async () => {
		const defaultValues = {
			fullName: null,
			email: null,
			jobTitle: null,
			mobileNumber: null,
			warehouseIDs: null,
			branchIDs: null,
			shipmentTarget: null,
			capacity: null,
			onDuty: false,
			avatarPath: null,
			lastLatLonUpdated: null,
			cashBalance: null,
			currency: Currency,
			lat: null,
			lng: null
		};

		if (!id) {
			return defaultValues;
		}

		try {
			const response = await axiosPrivate.post(OTOFLEX.GET_DRIVER, {
				ID: id
			});
			const { data, status } = response;

			if (status !== 200) {
				showError();
				handleCloseModal();
				return;
			}
			return {
				...defaultValues,
				...data,
				currency: data.currency ?? Currency
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
			handleCloseModal();
		}
	}, [id, Currency]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		setValue,
		watch,
		clearErrors,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful && !isMinimizing);

	const onSubmit = async (data) => {
		try {
			const response = await axiosPrivate.post(id ? OTOFLEX.UPDATE_DRIVER : OTOFLEX.ADD_DRIVER, {
				ID: id,
				fullName: data.fullName,
				email: data.email,
				jobTitle: data.jobTitle,
				mobileNumber: data.mobileNumber,
				warehouseIDs: data.warehouseIDs,
				branchIDs: data.branchIDs,
				shipmentTarget: data.shipmentTarget,
				capacity: data.capacity,
				avatarPath: data.avatarPath
			});

			if (response.status === 200) {
				showSuccess();
				setDriver && setDriver(response.data.ID);
				dispatch(updateDynamicListData(DYNAMICLISTS.OTO_FLEX_DRIVERS));
				!id && NiceModal.show(ResetPasswordForm, { resetLink: response.data.link, email: data.email });
				handleCloseModal();
			}
		} catch (error) {
			if (error.response?.data?.code === 'error.F010') {
				showError({
					header: 'emailAlreadyExists',
					message: 'error.F010'
				});
			} else if (error.response?.data?.code === 'error.B008') {
				showError({
					header: 'mobileNumberAlreadyExists',
					message: 'error.B008'
				});
			} else {
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
				AppLogger.error(error);
			}
		}
	};

	const currencyWatcher = watch('currency');
	const whAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);
	const isAuthorized = useAccessControl();

	return (
		<Modal
			fullwidth
			maxWidth="medium"
			name={modalName}
			className="modal-driver"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={mode === 'edit' ? 'editDriver' : mode === 'view' ? 'viewDriver' : 'addDriver'}
					tooltip={['edit', 'view'].includes(mode) ? watch('fullName') : undefined}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">
					{mode === 'edit' ? 'editDriver' : mode === 'view' ? 'viewDriver' : 'addDriver'}
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods} borderless viewPrintButton={viewMode}>
					<FormItem colSpan={defaultColSpan} label="name" required isLoading={isLoading}>
						<StringInput name="fullName" disabled={viewMode} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="email" required isLoading={isLoading}>
						<StringInput name="email" disabled={viewMode} />
					</FormItem>

					{!setDriver && (
						<FormItem colSpan={defaultColSpan} label="mobileNumber" isLoading={isLoading}>
							<PhoneNumber
								name="mobileNumber"
								value={watch('mobileNumber')}
								onChange={(value) => {
									setValue('mobileNumber', value, { shouldValidate: true, shouldDirty: true });
								}}
								disabled={viewMode}
							/>
						</FormItem>
					)}
					{!setDriver && (
						<FormItem
							colSpan={defaultColSpan}
							label="shipmentTarget"
							isLoading={isLoading}
							toolTip="shipmentTarget.description">
							<IntegerInput name="shipmentTarget" disabled={viewMode} />
						</FormItem>
					)}
					<FormItem
						colSpan={defaultColSpan}
						label="assignedWarehouses"
						isLoading={isLoading}
						required={!watch('branchIDs') || watch('branchIDs').length <= 0}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
							name="warehouseIDs"
							value={watch('warehouseIDs')}
							setValue={(value) => {
								setValue('warehouseIDs', value?.map((item) => item.ID) ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							firstItem={
								isAuthorized('/autocomplete/warehouse/addANewWarehouse')
									? {
											action: () =>
												NiceModal.show(WarehouseFormShort, {
													setWarehouse: (value) => {
														setValue(
															`warehouseIDs`,
															[...(watch('warehouseIDs') ?? []), value],
															{
																shouldValidate: true,
																shouldDirty: true
															}
														);
													}
												}),
											label: <Typography color="var(--blue)">addANewWarehouse</Typography>
										}
									: undefined
							}
							columns={whAutocompleteColumns}
							selectionLabelAccessor={['Name']}
							multipleSelection
							disabled={viewMode}
						/>
					</FormItem>

					<FormItem
						colSpan={defaultColSpan}
						label="assignedBranches"
						isLoading={isLoading}
						roleName="/autocomplete/branch">
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.BRANCHES}
							name="branchIDs"
							value={watch('branchIDs')}
							setValue={(value) => {
								setValue('branchIDs', value?.map((item) => item.ID) ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
								value?.length > 0 && clearErrors('warehouseIDs');
							}}
							firstItem={
								isAuthorized('/autocomplete/branch/addANewBranch')
									? {
											action: () =>
												NiceModal.show(BranchFormshort, {
													setBranch: (value) => {
														setValue(`branchIDs`, [...(watch('branchIDs') ?? []), value], {
															shouldValidate: true,
															shouldDirty: true
														});
													}
												}),
											label: <Typography color="var(--blue)">addANewBranch</Typography>
										}
									: undefined
							}
							columns={whAutocompleteColumns}
							selectionLabelAccessor={['Name']}
							multipleSelection
							disabled={viewMode}
						/>
					</FormItem>

					{!setDriver && (
						<FormItem
							colSpan={defaultColSpan}
							label="capacity"
							isLoading={isLoading}
							toolTip="capacity.description">
							<IntegerInput disabled={viewMode} name="capacity" />
						</FormItem>
					)}

					{!setDriver && id && (
						<>
							<FormItem colSpan={defaultColSpan} label="cashBalance" isLoading={isLoading}>
								<CurrencyInput
									disabled
									name="cashBalance"
									value={currencyWatcher}
									setValue={(value) => {
										setValue('currency', value, { shouldValidate: true, shouldDirty: true });
									}}
								/>
							</FormItem>
							<FormItem colSpan={defaultColSpan} label="lastLatLonUpdated" isLoading={isLoading}>
								<DateInput
									name="lastLatLonUpdated"
									disabled
									value={watch('lastLatLonUpdated')}
									setValue={(value) => {
										setValue('lastLatLonUpdated', value, {
											shouldValidate: true,
											shouldDirty: true
										});
									}}
								/>
							</FormItem>
							<FormItem colSpan={smallColSpan} label="lat" isLoading={isLoading}>
								<IntegerInput disabled name="lat" />
							</FormItem>

							<FormItem colSpan={smallColSpan} label="lon" isLoading={isLoading}>
								<IntegerInput disabled name="lng" />
							</FormItem>
							<FormItem colSpan={defaultColSpan} label="onDuty" isLoading={isLoading}>
								<SwitchInput className="mt-3" formMode viewMode name="onDuty" />
							</FormItem>
						</>
					)}
					{!setDriver && (
						<FormItem colSpan={defaultColSpan} label="image" isLoading={isLoading}>
							<ImageInput
								placeholder="selectFile"
								name="avatarPath"
								value={watch('avatarPath')}
								setValue={(value) => {
									setValue('avatarPath', value, { shouldValidate: true, shouldDirty: true });
								}}
								disabled={viewMode}
								viewMode={viewMode}
							/>
						</FormItem>
					)}
				</Form>
			</ModalContent>

			{!viewMode && (
				<ModalActions>
					<Button label="cancel" variant="outline" onClick={handleCloseModal} />

					<Button
						loading={isLoading}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						label={mode === 'edit' ? 'save' : 'button.add'}
					/>
				</ModalActions>
			)}
		</Modal>
	);
});

export default DriverForm;
